"use client";

import {
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
} from "react";
import { Check, Loader2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface UnsavedChangesAlertProps {
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  onUndo: () => void;
  onSave: (e?: FormEvent) => void;
  className?: string;
}

export function UnsavedChangesAlert({
  hasUnsavedChanges,
  isSaving,
  onUndo,
  onSave,
  className,
}: UnsavedChangesAlertProps) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [barStyle, setBarStyle] = useState<CSSProperties>({});

  useLayoutEffect(() => {
    const panel = anchorRef.current?.parentElement;
    if (!panel) return;

    const update = () => {
      const rect = panel.getBoundingClientRect();
      setBarStyle({
        left: rect.left,
        width: rect.width,
      });
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(panel);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [hasUnsavedChanges]);

  return (
    <>
      <div
        ref={anchorRef}
        className="pointer-events-none absolute h-0 w-0"
        aria-hidden
      />
      <div
        className={cn(
          "pointer-events-none fixed bottom-6 z-50 px-0",
          className
        )}
        style={barStyle}
        aria-hidden={!hasUnsavedChanges}
      >
        <div
          className={cn(
            "flex w-full items-center justify-between gap-6 rounded-2xl border border-border bg-card px-5 py-3 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1),0_12px_40px_-4px_rgba(0,0,0,0.18)] transition-all duration-300 ease-out dark:border-border dark:shadow-[0_4px_6px_-1px_rgba(0,0,0,0.4),0_16px_48px_-4px_rgba(0,0,0,0.55)]",
            hasUnsavedChanges
              ? "pointer-events-auto translate-y-0 opacity-100"
              : "translate-y-4 opacity-0"
          )}
        >
          <div className="flex min-w-0 items-center gap-2.5">
            <span
              className="size-2.5 shrink-0 rounded-full bg-amber-500"
              aria-hidden
            />
            <p className="truncate text-sm font-medium text-foreground">
              Unsaved changes
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-2.5">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onUndo}
              disabled={isSaving}
              className="h-9 gap-2 rounded-full px-4"
            >
              <RotateCcw className="size-4" />
              Discard
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={isSaving}
              onClick={(e) => onSave(e)}
              className="h-9 gap-2 rounded-full px-5"
            >
              {isSaving ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Check className="size-4" />
              )}
              {isSaving ? "Saving..." : "Save changes"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
